// @story HIR-HR2
// StageAssessmentPanel — one stage's assessment, designed and approved in the
// stage's own panel (the job's Assessment Pipeline tab, "Configure
// assessment"). Shohini's walk-through, 29 Oct 2026:
//
//   - design ONE stage at a time: "Draft with AI" asks the Assessment Designer
//     for this stage only (a new paper, or a fresh version of the approved
//     one); "Write it myself" opens the editor for this stage;
//   - either way it is a proposal: nothing reaches a candidate until a person
//     with the approving permission (recruitment:admin, for now the school's
//     administrator) approves it;
//   - an AI draft the person already asked for is shown, ready to review;
//   - the approver approves it, or sends it back with a reason, right here.
//
// GET  /assessment-proposals/stage/:jobId/:stageId   what this panel shows
// POST /assessment-proposals/design { jobId, stageId }   Draft with AI
// POST /assessment-proposals/own    { jobId, stageId, interview }   own AI interview questions
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { Button, Card, Input, StatusPill } from '../../ds';
import { ProposalView } from './ProposalReview';

const day = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const byWhom = (p) => (p.writtenBy === 'person' ? p.draftedBy : `the ${p.draftedBy || 'Assessment Designer'}`);

/* "Write it myself" for an AI interview stage: the questions the AI voice
   asks, one per line. The written test has its own editor (the Questions tab). */
function OwnInterviewQuestions({ jobId, stageId, onSaved, onCancel }) {
  const [rows, setRows] = useState(['', '', '']);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post('/assessment-proposals/own', { jobId, stageId, interview: { questions: rows.map((r) => r.trim()).filter(Boolean) } });
      toast.success('Saved for approval. The stage waits until the approver decides.');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save it for approval.'); } finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map((r, i) => (
        <Input key={i} id={`own-q-${stageId}-${i}`} label={`Question ${i + 1}`} value={r}
          onChange={(e) => setRows((all) => all.map((x, j) => (j === i ? e.target.value : x)))} />
      ))}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button variant="ghost" size="sm" onClick={() => setRows((all) => [...all, ''])}>Add a question</Button>
        <span style={{ flex: 1 }} />
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={save} loading={busy} disabled={!rows.some((r) => r.trim())}>Save for approval</Button>
      </div>
    </div>
  );
}

export default function StageAssessmentPanel({ jobId, stage, onApproved, onWriteMyself }) {
  const [state, setState] = useState(null);
  const [canApprove, setCanApprove] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [writingInterview, setWritingInterview] = useState(false);
  const isInterview = stage?.type === 'ai_audio_interview';

  const load = useCallback(() => api.get(`/assessment-proposals/stage/${jobId}/${stage.stageId}`)
    .then((r) => { setState(r.data?.data || {}); setCanApprove(!!r.data?.canApprove); })
    .catch(() => setState({})), [jobId, stage?.stageId]);
  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); if (onApproved) onApproved(); };

  const draftWithAi = async () => {
    setDrafting(true);
    try {
      await api.post('/assessment-proposals/design', { jobId, stageId: stage.stageId });
      toast.success('The Assessment Designer has drafted it. Read it below, then save it for approval.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'The Assessment Designer could not draft it just now.');
    } finally { setDrafting(false); }
  };

  if (!state) return <Card padding="16px"><span style={{ color: 'var(--text-muted)' }}>Loading this stage's assessment…</span></Card>;
  const { pending, myDraft, approved, sentBack } = state;
  // A send-back is news only until something newer is approved or waiting.
  const showSentBack = sentBack && !pending && (!approved || new Date(sentBack.decidedAt) > new Date(approved.decidedAt));
  const what = isInterview ? 'AI interview questions' : 'written test';

  return (
    <section aria-label="Design and approve this stage's assessment" style={{ display: 'grid', gap: 12 }}>
      <Card padding="16px">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 600, margin: 0 }}>This stage's {what}</h3>
          {pending ? <StatusPill tone="warning">Waiting for approval</StatusPill>
            : approved ? <StatusPill tone="success">Approved</StatusPill>
              : <StatusPill tone="neutral">Not designed yet</StatusPill>}
        </div>
        <p style={{ margin: '8px 0 0', fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
          {approved && `Written by ${byWhom(approved)} and approved by ${approved.decidedBy} on ${day(approved.decidedAt)}. `}
          {pending && `A ${what} written by ${byWhom(pending)} is waiting for the school's approver. Until it is approved this stage does not reach candidates. `}
          {!approved && !pending && 'Nothing reaches a candidate until a person with the approving permission approves it.'}
        </p>
        {showSentBack && (
          <p role="status" style={{ margin: '8px 0 0', fontSize: 'var(--fs-body-sm)', color: 'var(--status-danger-text)' }}>
            Sent back by {sentBack.decidedBy} on {day(sentBack.decidedAt)}: “{sentBack.decisionNote}”
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <Button size="sm" onClick={draftWithAi} loading={drafting}>
            {drafting ? 'Drafting — this takes a minute or two…' : approved ? 'Draft a fresh version with AI' : 'Draft with AI'}
          </Button>
          <Button size="sm" variant="secondary" disabled={drafting}
            onClick={() => (isInterview ? setWritingInterview(true) : onWriteMyself && onWriteMyself())}>
            Write it myself
          </Button>
        </div>
        {writingInterview && (
          <div style={{ marginTop: 12 }}>
            <OwnInterviewQuestions jobId={jobId} stageId={stage.stageId}
              onSaved={() => { setWritingInterview(false); load(); }} onCancel={() => setWritingInterview(false)} />
          </div>
        )}
      </Card>

      {myDraft && (
        <div style={{ display: 'grid', gap: 8 }}>
          <h4 style={{ fontSize: 'var(--fs-body-md)', fontWeight: 600, margin: 0 }}>Your AI draft, ready to review</h4>
          <ProposalView key={myDraft.proposalId} proposalId={myDraft.proposalId} onChanged={changed} />
        </div>
      )}
      {pending && (
        <div style={{ display: 'grid', gap: 8 }}>
          <h4 style={{ fontSize: 'var(--fs-body-md)', fontWeight: 600, margin: 0 }}>
            {canApprove ? 'Waiting for your approval' : 'Waiting for the approver'}
          </h4>
          <ProposalView key={pending.proposalId} proposalId={pending.proposalId} onChanged={changed} />
        </div>
      )}
    </section>
  );
}
